/**
 * Textos do cancelamento da assinatura (chamado 7116). A REGRA — quando o
 * cancelamento passa a valer e se já foi pedido — vem pronta do backend em
 * `subscription.cancellation`; aqui só se montam as frases aprovadas.
 */
import { formatBrasilia, type ApiDateValue } from '@/lib/brasiliaDate';
import type { CancellationReason, CancellationSummary } from '@/services/account.service';

const dataCompleta = (dia: ApiDateValue): string => formatBrasilia(dia, 'dd/MM/yyyy');

export const cancellationTitle = (planName: string): string => `Cancelar assinatura do ${planName}?`;

/** Subtítulo do modal: lembra o que se perde, para a pessoa repensar. */
export const CANCELLATION_LOSS_TEXT =
  'Ao cancelar, você perderá o acesso aos recursos Premium que hoje ajudam você a organizar, acompanhar e planejar suas finanças.';

export const cancellationKeepText = (effectiveDate: ApiDateValue, inTrial: boolean): string =>
  `Você continuará aproveitando todos os recursos Premium até o fim do seu ${
    inTrial ? 'período de teste' : 'período contratado'
  } em ${dataCompleta(effectiveDate)}.`;

export const CANCELLATION_AFTER_TEXT =
  'Após essa data, sua conta passará automaticamente para o Plano Gratuito. Seus dados e históricos continuarão 100% salvos e seguros.';

export const cancellationScheduledText = (effectiveDate: ApiDateValue): string =>
  `Assinatura cancelada. Seus recursos Premium continuam até ${dataCompleta(effectiveDate)}; depois, sua conta passa para o Plano Gratuito.`;

/** Ida ao gratuito pedida no teste: fica para o fim dele (chamado 7116). */
export const freePlanScheduledText = (effectiveDate: ApiDateValue): string =>
  `Mudança para o Plano Gratuito agendada. Você continua no período de teste até ${dataCompleta(effectiveDate)}; depois, sua conta passa para o Plano Gratuito.`;

/** Aviso do card do plano conforme o tipo do agendamento (regra do backend). */
export const scheduledNoticeText = (agendamento: CancellationSummary): string | null => {
  if (!agendamento.effective_date) return null;
  return agendamento.kind === 'downgrade'
    ? freePlanScheduledText(agendamento.effective_date)
    : cancellationScheduledText(agendamento.effective_date);
};

/** Confirmação da troca para o gratuito durante o teste: até quando vai o
 * teste e o que acontece depois dele. */
export const trialFreePlanChangeNotice = (
  trialEnd: ApiDateValue,
  currentPlanName: string,
  freePlanName: string,
): string =>
  `Você está em período de teste até ${dataCompleta(trialEnd)} e continua com todos os recursos do ${currentPlanName} até essa data. Depois dela, sua conta passa automaticamente para o ${freePlanName}, sem nenhuma cobrança, e seus dados continuam salvos.`;

/** Mesmo texto do 409 do backend: no gratuito o botão só informa, sem modal. */
export const FREE_PLAN_CANCEL_MESSAGE = 'Você já está no Plano Gratuito; não há assinatura a cancelar.';

/** Motivos aprovados, na ordem do select; o valor é a categoria do backend. */
export const CANCELLATION_REASONS: ReadonlyArray<{ value: CancellationReason; label: string }> = [
  { value: 'budget', label: 'O valor atual não cabe no meu orçamento no momento' },
  { value: 'free_plan_enough', label: 'As funcionalidades do Plano Gratuito já atendem ao que preciso' },
  { value: 'low_usage', label: 'Não uso a plataforma com frequência suficiente para justificar o plano pago' },
  { value: 'missing_features', label: 'Sinto falta de recursos/funcionalidades essenciais no plano pago' },
  { value: 'technical_issues', label: 'Encontrei problemas técnicos, bugs ou tive suporte insatisfatório' },
  { value: 'switching_tool', label: 'Estou migrando para outra ferramenta de gestão financeira' },
  { value: 'other', label: 'Outro motivo' },
];

/**
 * Botão vermelho contornado dos cancelamentos. Sobrescreve o hover do
 * `outline` (texto branco do accent), que some sobre o fundo vermelho claro.
 */
export const CANCEL_BUTTON_CLASS =
  'text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20 dark:hover:text-red-300';
